import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { BASE } from '../services/api';
import { toast } from '../utils/toastBus';
import { Button, Card } from '../components/ui';
import { BrandContext } from '../context/BrandContext';
import { AuthContext } from '../context/AuthContext';
import { AD_PRICE_USD_FALLBACK, formatAdPrice } from '../content/exampleAds';
import { isPurchaseSession, readPurchaseSession, rememberPurchaseSession, clearPurchaseSession } from '../utils/adPurchaseRecovery';
import { trackBeginCheckout, trackPurchase } from '../utils/adConversions';
import createStyles from './Create.module.css';

const CANNED_AD_SCRIPT =
  'This weekend only — twenty percent off your first visit. Book online or walk in. We can’t wait to see you.';

const MIN_SCRIPT_LENGTH = 10;
const VIDEO_AD_PRICE_USD_FALLBACK = 39;
const VIDEO_PAID_KEY = 'loudest_video_ad_pending';
const SARAH_ID = 'EXAVITQu4vr4xnSDxMaL';
const SAMPLE_LINE = 'Your weekend sale, in this voice.';
const PENDING_KEY = 'loudest_ad_pending';

const PREFERRED_VOICES = ['Sarah', 'Brian', 'Daniel', 'Jessica', 'Alice', 'Liam'];
const FALLBACK_VOICES = [
  { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Sarah' },
  { id: 'nPczCjzI2devNBz1zQrb', name: 'Brian' },
  { id: 'onwK4e9ZLuTAKqWW03F9', name: 'Daniel' },
  { id: 'cgSgspJ2msm6clMCkdW9', name: 'Jessica' },
  { id: 'Xb7hH8MSUJpSbSDYk0k2', name: 'Alice' },
  { id: 'TX3LPaxmHKxFdv7VOQHJ', name: 'Liam' },
];

function shortVoiceName(raw) {
  const name = String(raw || '').trim();
  if (!name) return '';
  return name.split(' - ')[0].split(' – ')[0].split('—')[0].trim();
}

function pickVoices(items) {
  const list = (Array.isArray(items) ? items : [])
    .map((v) => ({
      id: v?.id || v?.voice_id || v?.voiceId,
      name: shortVoiceName(v?.name || v?.voice_name),
    }))
    .filter((v) => v.id && v.name && !/^lilirose/i.test(v.name));
  const chosen = [];
  for (const name of PREFERRED_VOICES) {
    const hit = list.find((v) => v.name.toLowerCase() === name.toLowerCase());
    if (hit && !chosen.some((c) => c.id === hit.id)) chosen.push(hit);
  }
  for (const v of list) {
    if (chosen.length >= 6) break;
    if (!chosen.some((c) => c.id === v.id)) chosen.push(v);
  }
  return chosen.length ? chosen.slice(0, 6) : FALLBACK_VOICES;
}

function loadDraft() {
  const empty = { scriptText: '', voiceId: SARAH_ID };
  try {
    const raw = window.localStorage.getItem('streamcreator_draft');
    if (!raw) return empty;
    const d = JSON.parse(raw);
    let scriptText = String(d?.scriptText || '');
    if (scriptText.trim() === CANNED_AD_SCRIPT.trim()) scriptText = '';
    return { scriptText, voiceId: d?.voiceId || SARAH_ID };
  } catch {
    return empty;
  }
}

function readPending() {
  try {
    const d = JSON.parse(window.sessionStorage.getItem(PENDING_KEY) || 'null');
    if (!d?.audioPath) return null;
    return { _id: d.id, title: d.title || 'Your voice ad', audioPath: d.audioPath };
  } catch {
    return null;
  }
}

function rememberAd(item) {
  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({
      id: item?._id || item?.id || '',
      title: item?.title || 'Your voice ad',
      audioPath: item?.audioPath || '',
    }));
  } catch {}
}

function mediaUrl(path) {
  const rel = String(path || '');
  if (!rel) return '';
  if (rel.startsWith('http://') || rel.startsWith('https://')) return rel;
  return `${BASE}${rel.startsWith('/') ? rel : `/${rel}`}`;
}

function validEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}
function recoveryStorage() {
  try { return window.sessionStorage; } catch { return null; }
}

function paymentRequired(res) {
  return res?.error === 'payment_required' || res?.status === 402;
}

export default function StreamCreator() {
  const { profile: brandProfile, profileExists } = useContext(BrandContext);
  const { user } = useContext(AuthContext);
  const draft = loadDraft();
  const [title, setTitle] = useState('');
  const [scriptText, setScriptText] = useState(draft.scriptText);
  const [scriptTouched, setScriptTouched] = useState(false);
  const [voices, setVoices] = useState(FALLBACK_VOICES);
  const [voiceId, setVoiceId] = useState(draft.voiceId || SARAH_ID);
  const [sampling, setSampling] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [signedAudioUrl, setSignedAudioUrl] = useState('');
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [payPurpose, setPayPurpose] = useState('');
  const [payNote, setPayNote] = useState('');
  const [priceUsd, setPriceUsd] = useState(AD_PRICE_USD_FALLBACK);
  const [checkingOut, setCheckingOut] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [paidDownload, setPaidDownload] = useState(null);
  const [paidGenerationReady, setPaidGenerationReady] = useState(false);
  const paidGenerationRef = useRef(false);
  const [videoAdPrice, setVideoAdPrice] = useState(VIDEO_AD_PRICE_USD_FALLBACK);
  const [videoCheckingOut, setVideoCheckingOut] = useState(false);
  const [videoClaiming, setVideoClaiming] = useState(false);
  const [videoError, setVideoError] = useState('');
  const [videoResult, setVideoResult] = useState(null);
  const videoClaimStartedRef = useRef(false);
  const audioRef = useRef(null);
  const autoplayWarnedRef = useRef(false);
  const scriptEnteredTrackedRef = useRef(false);
  const claimStartedRef = useRef(false);
  const claimSessionRef = useRef('');
  const purchaseSavedRef = useRef(false);
  const [duration, setDuration] = useState(0);
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const [autoPlayOnCreate, setAutoPlayOnCreate] = useState(() => {
    try {
      const raw = window.localStorage.getItem('autoplay_on_create');
      if (raw === null) return true;
      return JSON.parse(raw) === true;
    } catch { return true; }
  });
  const [jobId, setJobId] = useState('');
  const [jobStatus, setJobStatus] = useState('');
  const [jobProgress, setJobProgress] = useState(0);
  const priceLabel = formatAdPrice(priceUsd);

  const recoverPurchase = useCallback(async () => {
    const sessionId = claimSessionRef.current;
    setClaiming(true);
    setClaimError('');
    try {
      const claim = await api.claimAd(sessionId ? { sessionId } : {});
      if (!claim?.ok) throw new Error('delivery_not_confirmed');
      if (isPurchaseSession(claim.purchaseSessionId)) {
        claimSessionRef.current = claim.purchaseSessionId;
        purchaseSavedRef.current = rememberPurchaseSession(recoveryStorage(), claim.purchaseSessionId);
      }
      if (claim?.priceUsd) setPriceUsd(Number(claim.priceUsd));
      trackPurchase({
        transactionId: claim.purchaseSessionId || sessionId,
        value: claim.priceUsd || priceUsd,
        purpose: claim.purpose,
      });
      if (claim.purpose === 'another') {
        paidGenerationRef.current = true;
        setPaidGenerationReady(true);
        setPaidDownload(null);
        setPayPurpose('');
        toast({ type: 'success', message: 'Payment received. Your next clip includes its download.' });
      } else if (claim.purpose === 'download' && claim.audioPath) {
        paidGenerationRef.current = false;
        setPaidGenerationReady(false);
        const pending = readPending();
        setResult({ _id: claim.streamId, title: pending?.title || 'Your voice ad', audioPath: claim.audioPath });
        setPaidDownload({ streamId: claim.streamId, audioPath: claim.audioPath });
        toast({
          type: 'success',
          message: claim.emailed
            ? 'Payment received. Your audio is ready below, and we emailed the file.'
            : 'Payment received. Your audio is ready to download below.',
        });
      } else {
        throw new Error('delivery_not_confirmed');
      }
      if (purchaseSavedRef.current) {
        const params = new URLSearchParams(window.location.search);
        params.delete('ad_paid');
        const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
        window.history.replaceState({}, '', next);
      }
    } catch {
      setClaimError('We could not confirm delivery. Retry below before making another payment. Keep this tab open to retain your recovery reference.');
    } finally {
      setClaiming(false);
    }
  }, []);

  const claimVideoAdSession = useCallback(async (sessionId) => {
    setVideoClaiming(true);
    setVideoError('');
    try {
      const res = await api.claimVideoAd({ sessionId });
      if (!res?.ok || !res?.videoPath) {
        setVideoError(res?.message || 'We could not confirm that payment. Retry below.');
        return;
      }
      trackPurchase({ transactionId: res.purchaseSessionId || sessionId, value: videoAdPrice, purpose: 'video_ad' });
      setVideoResult({ streamId: res.streamId, videoUrl: mediaUrl(res.videoPath) });
    } catch {
      setVideoError('We could not confirm that payment. Retry below.');
    } finally {
      setVideoClaiming(false);
    }
  }, [videoAdPrice]);

  useEffect(() => {
    let canceled = false;
    fetch(`${BASE}/api/payments/video-ad-offer`, { credentials: 'include' })
      .then(async (response) => {
        const offer = await response.json();
        if (!canceled && response.ok && Number(offer?.priceUsd) > 0) setVideoAdPrice(Number(offer.priceUsd));
      })
      .catch(() => {});
    return () => { canceled = true; };
  }, []);

  useEffect(() => {
    if (videoClaimStartedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const paid = params.get('video_paid');
    if (!paid) return;
    videoClaimStartedRef.current = true;
    if (paid === 'cancel') {
      params.delete('video_paid');
      window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? `?${params}` : ''}`);
      toast({ type: 'info', message: 'Video checkout canceled.' });
      return;
    }
    params.delete('video_paid');
    params.delete('streamId');
    window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? `?${params}` : ''}`);
    void claimVideoAdSession(paid);
  }, [claimVideoAdSession]);

  useEffect(() => {
    let canceled = false;
    fetch(`${BASE}/api/payments/ad-offer`, { credentials: 'include' })
      .then(async (response) => {
        const offer = await response.json();
        if (!canceled && response.ok && Number(offer?.priceUsd) > 0) setPriceUsd(Number(offer.priceUsd));
      })
      .catch(() => {});
    return () => { canceled = true; };
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/api/dj/voices`, { credentials: 'include' });
        const body = await r.json().catch(() => null);
        const picked = pickVoices(body?.items);
        if (!cancel && r.ok && picked.length) {
          setVoices(picked);
          setVoiceId((cur) => (picked.some((v) => v.id === cur) ? cur : picked[0].id));
        }
      } catch {}
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (profileExists && brandProfile?.name && !title) {
      setTitle(`${brandProfile.name} voice ad`);
    }
  }, [brandProfile?.name, profileExists, title]);

  useEffect(() => {
    try { window.localStorage.setItem('autoplay_on_create', JSON.stringify(!!autoPlayOnCreate)); } catch {}
  }, [autoPlayOnCreate]);

  useEffect(() => {
    const h = setTimeout(() => {
      try {
        window.localStorage.setItem('streamcreator_draft', JSON.stringify({
          scriptText,
          voiceId,
          t: Date.now(),
        }));
      } catch {}
    }, 400);
    return () => clearTimeout(h);
  }, [scriptText, voiceId]);

  useEffect(() => {
    (async () => {
      try {
        setSignedAudioUrl('');
        const rel = typeof result?.audioPath === 'string' ? result.audioPath : '';
        if (!rel) return;
        if (rel.startsWith('/uploads/')) {
          const { url } = await api.signDownload(rel, 5 * 60 * 1000);
          if (url) { setSignedAudioUrl(url); return; }
        }
        setSignedAudioUrl(mediaUrl(rel));
      } catch {
        const rel = typeof result?.audioPath === 'string' ? result.audioPath : '';
        if (rel) setSignedAudioUrl(mediaUrl(rel));
      }
    })();
  }, [result?.audioPath]);

  useEffect(() => {
    (async () => {
      try {
        if (!shouldAutoplay || !signedAudioUrl) return;
        const el = audioRef.current;
        if (!el) return;
        await el.play();
        setShouldAutoplay(false);
      } catch {
        if (!autoplayWarnedRef.current) {
          autoplayWarnedRef.current = true;
          try { toast({ type: 'info', message: 'Autoplay was blocked. Press play on the player.' }); } catch {}
        }
      }
    })();
  }, [signedAudioUrl, shouldAutoplay]);

  useEffect(() => {
    if (claimStartedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const paid = params.get('ad_paid');
    const pending = readPending();
    if (pending) setResult(pending);
    claimStartedRef.current = true;
    if (paid === 'cancel') {
      params.delete('ad_paid');
      window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? `?${params}` : ''}`);
      toast({ type: 'info', message: 'Checkout canceled.' });
      return;
    }
    const sessionId = isPurchaseSession(paid) ? paid : readPurchaseSession(recoveryStorage());
    if (!sessionId) return;
    claimSessionRef.current = sessionId;
    purchaseSavedRef.current = rememberPurchaseSession(recoveryStorage(), sessionId);
    void recoverPurchase();
  }, [recoverPurchase]);

  const deriveTitle = (text) => {
    const s = String(text || '').trim().replace(/\s+/g, ' ');
    if (!s) return 'Voice ad';
    return s.length <= 80 ? s : `${s.slice(0, 79)}…`;
  };

  const retainPurchase = (data) => {
    if (!isPurchaseSession(data?.purchaseSessionId)) return;
    claimSessionRef.current = data.purchaseSessionId;
    purchaseSavedRef.current = rememberPurchaseSession(recoveryStorage(), data.purchaseSessionId);
    paidGenerationRef.current = true;
    setPaidGenerationReady(true);
  };

  const acceptAd = ({ item, downloadIncluded }) => {
    if ((paidGenerationRef.current && downloadIncluded !== true)
      || (downloadIncluded && !item?.audioPath)) throw new Error('paid_delivery_not_confirmed');
    paidGenerationRef.current = false;
    setPaidGenerationReady(false);
    setPaidDownload(downloadIncluded ? { streamId: item._id || item.id, audioPath: item.audioPath } : null);
    if (!downloadIncluded) {
      clearPurchaseSession(recoveryStorage());
      claimSessionRef.current = '';
    }
    setResult(item);
    setShouldAutoplay(!!autoPlayOnCreate);
    rememberAd(item);
    setPayPurpose('');
    toast({ type: 'success', message: 'Your ad is ready.' });
    try { api.trackEvent('generation_succeeded', { voiceId }); } catch {}
  };

  const pollJob = async (id) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < 120000) {
      let snap = null;
      try { snap = await api.getJob(id); } catch { snap = null; }
      if (snap) {
        if (typeof snap.progress === 'number') setJobProgress(Math.max(0, Math.min(100, Number(snap.progress) || 0)));
        if (snap.status) setJobStatus(String(snap.status));
        if (snap.status === 'error') throw new Error(snap.error || 'Job failed');
        if (snap.status === 'done' && snap.result?.item) return snap.result;
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 1000));
    }
    throw new Error('Job timed out');
  };

  const doCreate = async () => {
    if (claiming || claimError) return;
    if (scriptText.trim().length < MIN_SCRIPT_LENGTH) {
      setScriptTouched(true);
      return;
    }
    try { api.trackEvent('generate_clicked', { voiceId }); } catch {}
    setLoading(true);
    setError('');
    setResult(null);
    setPayNote('');
    setJobId('');
    setJobStatus('');
    setJobProgress(0);
    const payload = {
      title: title || deriveTitle(scriptText),
      scriptText,
      voiceId,
      visibility: 'private',
      autoGenerateImage: false,
      silencePaddingMs: 1000,
    };
    try {
      const asyncRes = await api.createStreamAsync(payload);
      retainPurchase(asyncRes);
      if (asyncRes?.error === 'purchase_recovery_required' || asyncRes?.error === 'purchase_in_progress') {
        setClaimError('Your paid clip needs recovery. Retry below before making another payment.');
        return;
      }
      if (paymentRequired(asyncRes)) {
        setPayPurpose('another');
        if (Number(asyncRes.priceUsd) > 0) setPriceUsd(Number(asyncRes.priceUsd));
        return;
      }
      if (asyncRes?.jobId) {
        setJobId(asyncRes.jobId);
        setJobStatus('queued');
        const data = await pollJob(asyncRes.jobId);
        acceptAd(data);
        return;
      }
      if (asyncRes?.item) {
        acceptAd(asyncRes);
        return;
      }
      if (!asyncRes?.error) {
        const data = await api.post('/api/streams', payload);
        retainPurchase(data);
        if (data?.error === 'purchase_recovery_required' || data?.error === 'purchase_in_progress') {
          setClaimError('Your paid clip needs recovery. Retry below before making another payment.');
          return;
        }
        if (paymentRequired(data)) {
          setPayPurpose('another');
          if (Number(data.priceUsd) > 0) setPriceUsd(Number(data.priceUsd));
          return;
        }
        if (!data?.item) throw new Error('no item');
        acceptAd(data);
        return;
      }
      throw new Error(asyncRes.error || 'failed');
    } catch {
      if (claimSessionRef.current) {
        setClaimError('We could not confirm your paid clip. Retry purchase recovery below before making another payment.');
      }
      setError('Failed to create the ad');
      toast({ type: 'error', message: 'Failed to create the ad' });
      try { api.trackEvent('generation_failed', { voiceId }); } catch {}
    } finally {
      setLoading(false);
    }
  };

  const beginCheckout = async (purpose) => {
    if (claiming || claimError
      || (purpose === 'download' && paidDownload?.streamId === (result?._id || result?.id))) return;
    const clean = email.trim();
    if (!validEmail(clean)) {
      setEmailError('Enter an email for the receipt.');
      return;
    }
    if (purpose === 'download' && !(result?._id || result?.id)) {
      setPayNote('Generate the ad first, then download it.');
      return;
    }
    setEmailError('');
    setPayNote('');
    setCheckingOut(true);
    try {
      if (result) rememberAd(result);
      const body = { email: clean, purpose };
      if (claimSessionRef.current) body.purchaseSessionId = claimSessionRef.current;
      if (purpose === 'download') body.streamId = result._id || result.id;
      const res = await api.checkoutAd(body);
      if (res?.downloadIncluded === true && res.streamId === (result?._id || result?.id)) {
        setPaidDownload({ streamId: res.streamId, audioPath: result.audioPath });
        return;
      }
      if (['credit_available', 'purchase_recovery_required', 'purchase_in_progress'].includes(res?.error)) {
        setClaimError('You already have a paid purchase to recover. Retry recovery below before paying again.');
        return;
      }
      if (res?.error === 'stripe_unavailable' || res?.status === 503) {
        setPayNote('Payments are temporarily unavailable. You can still play the ad on this page.');
        return;
      }
      if (res?.url) {
        trackBeginCheckout({ value: priceUsd, purpose });
        window.location.assign(res.url);
        return;
      }
      setPayNote(res?.message || 'Could not start checkout. Please try again.');
    } finally {
      setCheckingOut(false);
    }
  };

  const makeVideoAd = async () => {
    const streamId = result?._id || result?.id;
    if (!streamId || videoCheckingOut) return;
    setVideoError('');
    setVideoCheckingOut(true);
    try {
      const res = await api.checkoutVideoAd({ streamId });
      if (res?.error === 'stripe_unavailable' || res?.status === 503) {
        setVideoError('Payments are temporarily unavailable. Please try again later.');
        return;
      }
      if (res?.error === 'unauthorized' || res?.status === 401) {
        setVideoError('Log in or create a free account first, then come back and make the video.');
        return;
      }
      if (res?.url) {
        trackBeginCheckout({ value: res.priceUsd || videoAdPrice, purpose: 'video_ad' });
        window.location.assign(res.url);
        return;
      }
      setVideoError(res?.message || 'Could not start checkout. Please try again.');
    } finally {
      setVideoCheckingOut(false);
    }
  };

  const sampleVoice = async (voice) => {
    setSampling(voice.id);
    try {
      const res = await api.djSpeak({ text: SAMPLE_LINE, voiceId: voice.id, voiceName: voice.name });
      if (res?.error || !res?.blob) {
        toast({ type: 'error', message: 'Could not play that sample.' });
        return;
      }
      const url = URL.createObjectURL(res.blob);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch {
      toast({ type: 'error', message: 'Could not play that sample.' });
    } finally {
      setSampling('');
    }
  };

  const trimmedScriptLength = scriptText.trim().length;
  const scriptError = trimmedScriptLength > 0 && trimmedScriptLength < MIN_SCRIPT_LENGTH
    ? `Script is too short — add a bit more detail (at least ${MIN_SCRIPT_LENGTH} characters).`
    : '';
  const isScriptValid = trimmedScriptLength >= MIN_SCRIPT_LENGTH;
  const audioUrl = signedAudioUrl || mediaUrl(result?.audioPath);

  return (
    <div className={createStyles.wrap}>
      <header className={createStyles.header}>
        <div>
          <h1 className={createStyles.title}>Create a voice ad</h1>
          <p className={createStyles.subtitle}>
            Try your first preview free. {priceLabel} per clip, including the MP3 download. No second download charge.
          </p>
        </div>
        <Link to="/discover" className={createStyles.examplesLink}>
          Hear examples →
        </Link>
      </header>

      {profileExists && (
        <Card hover className={createStyles.brandStrip}>
          <div className={createStyles.brandStripInner}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <p style={{ margin: 0, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--loud-lime)', fontWeight: 700 }}>Brand profile active</p>
              <h3 style={{ margin: '4px 0 8px', fontFamily: 'var(--font-display)' }}>{brandProfile?.name || 'Untitled brand'}</h3>
              <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>{brandProfile?.tagline || 'Customize your tagline to personalize intros.'}</p>
            </div>
            <Button variant="ghost" onClick={() => window.location.assign('/brand')}>
              Edit brand →
            </Button>
          </div>
        </Card>
      )}

      <Card className={createStyles.panel}>
        <form
          onSubmit={(e) => { e.preventDefault(); doCreate(); }}
          className={createStyles.form}
        >
          <div>
            <span className={createStyles.label} id="voice-label">Voice</span>
            <div className={createStyles.voiceGrid} role="group" aria-labelledby="voice-label">
              {voices.map((voice) => (
                <div key={voice.id} className={voice.id === voiceId ? createStyles.voiceOn : createStyles.voice}>
                  <button
                    type="button"
                    className={createStyles.voiceName}
                    aria-pressed={voice.id === voiceId}
                    onClick={() => {
                      setVoiceId(voice.id);
                      try { api.trackEvent('voice_selected', { voiceId: voice.id }); } catch {}
                    }}
                  >
                    {voice.name}
                  </button>
                  <button
                    type="button"
                    className={createStyles.sampleBtn}
                    onClick={() => sampleVoice(voice)}
                    disabled={sampling === voice.id}
                  >
                    {sampling === voice.id ? 'Playing…' : 'Sample'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className={createStyles.label} htmlFor="stream-script-text">
              Script
              <span className={createStyles.required}>*</span>
            </label>
            <textarea
              id="stream-script-text"
              value={scriptText}
              onChange={(e) => {
                setScriptText(e.target.value);
                if (!scriptEnteredTrackedRef.current && e.target.value.trim().length >= MIN_SCRIPT_LENGTH) {
                  scriptEnteredTrackedRef.current = true;
                  try { api.trackEvent('script_entered'); } catch {}
                }
              }}
              onBlur={() => setScriptTouched(true)}
              rows={6}
              required
              aria-invalid={scriptTouched && !!scriptError}
              aria-describedby="stream-script-hint stream-script-error"
              placeholder="Describe the deal or business. Example: This weekend only — 20% off your first visit..."
              className={createStyles.textarea}
            />
            <p id="stream-script-hint" className={createStyles.hint}>
              {scriptText.length} characters · keep it under ~30 seconds of speech
            </p>
            {scriptTouched && scriptError && (
              <p id="stream-script-error" role="alert" style={{ color: '#ffb0a0', fontSize: 13, marginTop: 4 }}>
                {scriptError}
              </p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <input id="auto-play-on-create" type="checkbox" checked={autoPlayOnCreate} onChange={(e) => setAutoPlayOnCreate(e.target.checked)} />
              <label htmlFor="auto-play-on-create" style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>Play when the ad is ready</label>
            </div>
          </div>

          <div className={createStyles.submitWrap}>
            <Button type="submit" loading={loading} disabled={!isScriptValid || claiming || !!claimError} fullWidth size="lg">
              {loading ? 'Generating your ad…' : 'Generate voice ad'}
            </Button>
          </div>
          {jobId && jobStatus && jobStatus !== 'done' && jobStatus !== 'error' && (
            <div className={createStyles.progressBox} role="status" aria-live="polite">
              <div className={createStyles.progressMeta}>
                <span>Generating audio…</span>
                <span>{jobProgress}%</span>
              </div>
              <div className={createStyles.progressTrack}>
                <div className={createStyles.progressFill} style={{ width: `${Math.max(0, Math.min(100, jobProgress))}%` }} />
              </div>
            </div>
          )}
          {error && (
            <div role="alert" style={{ marginTop: 12, padding: '12px 16px', borderRadius: 12, background: 'rgba(255, 77, 109, 0.12)', border: '1px solid rgba(255, 77, 109, 0.4)', color: '#ffb0a0', fontSize: 14 }}>
              {error} — please check your connection and try again.
            </div>
          )}
        </form>
      </Card>
      {(claiming || claimError) && (
        <Card title="Recover your purchase" className={createStyles.resultPanel}>
          {claiming ? (
            <p role="status">Confirming your payment and preparing your audio…</p>
          ) : (
            <>
              <p role="alert">{claimError}</p>
              <Button type="button" onClick={recoverPurchase}>Retry purchase recovery</Button>
            </>
          )}
        </Card>
      )}

      {payPurpose === 'another' && (
        <Card title="The next ad is paid" className={createStyles.resultPanel}>
          <p style={{ marginTop: 0, color: 'var(--color-text-secondary)' }}>
            Your free preview is used. Pay {priceLabel} once for another clip, including its MP3 download. Stripe sends the receipt.
          </p>
          <PayFields
            email={email}
            setEmail={setEmail}
            emailError={emailError}
            payNote={payNote}
            checkingOut={checkingOut}
            label={`Create + download for ${priceLabel}`}
            onSubmit={() => beginCheckout('another')}
          />
        </Card>
      )}

      {result && (
        <Card title={`Voice ad ready: ${result.title || 'Your ad'}`} className={createStyles.resultPanel}>
          {audioUrl ? (
            <audio
              ref={audioRef}
              onLoadedMetadata={() => {
                if (audioRef.current && isFinite(audioRef.current.duration)) setDuration(audioRef.current.duration);
              }}
              onCanPlay={async () => {
                if (!shouldAutoplay || !audioRef.current) return;
                try { await audioRef.current.play(); } catch {}
                setShouldAutoplay(false);
              }}
              controls
              autoPlay={shouldAutoplay}
              preload="metadata"
              src={audioUrl}
              style={{ width: '100%', height: 40, display: 'block' }}
            />
          ) : (
            <p style={{ color: '#fca5a5' }}>No audio was generated. Try again in a moment.</p>
          )}
          {duration > 0 && (
            <p className={createStyles.hint}>{Math.round(duration)} seconds</p>
          )}
          {paidDownload && paidDownload.streamId === (result._id || result.id) ? (
            <div>
              <p>Your purchase is confirmed. You do not need to pay again for this ad.</p>
              <a href={audioUrl} download target="_blank" rel="noopener noreferrer">
                Open or download your paid audio
              </a>
            </div>
          ) : paidGenerationReady ? (
            <p>Your paid clip includes its download. Generate your new clip above; no second payment is needed.</p>
          ) : !claiming && !claimError ? (
            <>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Enter an email and pay {priceLabel} to download the file. Stripe sends the receipt.
              </p>
              <PayFields
                email={email}
                setEmail={setEmail}
                emailError={emailError}
                payNote={payNote}
                checkingOut={checkingOut}
                label={`Download for ${priceLabel}`}
                onSubmit={() => beginCheckout('download')}
              />
              <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginTop: 8 }}>
                Making more than one a month? <Link to="/profile">Subscribe on your profile</Link> for a monthly
                clip allowance instead of paying per clip.
              </p>
            </>
          ) : null}
        </Card>
      )}

      {result && audioUrl && (
        <Card title="Turn this into a video" className={createStyles.resultPanel}>
          {videoResult && videoResult.streamId === (result._id || result.id) ? (
            <div>
              <p>Your video is ready.</p>
              <video controls preload="metadata" src={videoResult.videoUrl} style={{ width: '100%', maxWidth: 360, display: 'block', borderRadius: 12 }} />
              <a href={videoResult.videoUrl} download target="_blank" rel="noopener noreferrer">
                Download the video
              </a>
            </div>
          ) : videoClaiming ? (
            <p role="status">Building your video — this can take a little while…</p>
          ) : (
            <>
              <p style={{ marginTop: 0, color: 'var(--color-text-secondary)' }}>
                A ready-to-post vertical video with this voiceover, captions, and a branded background —
                for Reels, Stories, and TikTok. One-time, ${videoAdPrice}.
              </p>
              {videoError && <p role="alert" style={{ color: '#fca5a5' }}>{videoError}</p>}
              {user ? (
                <Button type="button" onClick={makeVideoAd} disabled={videoCheckingOut}>
                  {videoCheckingOut ? 'Starting checkout…' : `Make the video — $${videoAdPrice}`}
                </Button>
              ) : (
                <p style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>
                  <Link to="/login">Log in</Link> or <Link to="/register">create a free account</Link> to make the video.
                </p>
              )}
            </>
          )}
        </Card>
      )}
    </div>
  );
}

function PayFields({ email, setEmail, emailError, payNote, checkingOut, label, onSubmit }) {
  return (
    <div>
      <label className={createStyles.label} htmlFor="ad-email">Email for the receipt</label>
      <div className={createStyles.payRow}>
        <input
          id="ad-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@business.com"
          className={`${createStyles.field} ${createStyles.email}`}
        />
        <Button type="button" onClick={onSubmit} loading={checkingOut}>
          {label}
        </Button>
      </div>
      {emailError && <p role="alert" style={{ color: '#ffb0a0', fontSize: 13 }}>{emailError}</p>}
      {payNote && <p role="status" style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>{payNote}</p>}
    </div>
  );
}
